"use client"

import type React from "react"
import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Shield, Eye, EyeOff, AlertTriangle } from "lucide-react"
import { apiService } from "@/lib/api-service"
import { CONFIG } from "@/lib/config"

interface AdminLoginProps {
  onLoginSuccess: (userData: any) => void
}

interface LoginResponse {
  success: boolean
  data?: any
  error?: string
}

export function AdminLogin({ onLoginSuccess }: AdminLoginProps) {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [attemptCount, setAttemptCount] = useState(0)

  // Rate limiting - block after 3 failed attempts
  const maxAttempts = 3
  const isBlocked = attemptCount >= maxAttempts

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isBlocked) {
      setError("Too many failed attempts. Please refresh the page to try again.")
      return
    }

    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password")
      return
    }

    setIsLoading(true)
    setError("")

    try {
      const response: LoginResponse = await apiService.login(username.trim(), password)

      if (response.success && response.data) {
        // Reset attempt count on success
        setAttemptCount(0)
        onLoginSuccess(response.data)
      } else {
        setAttemptCount(prev => prev + 1)
        setError(response.error || "Invalid credentials. Please try again.")
      }
    } catch (error) {
      setAttemptCount(prev => prev + 1)
      console.error("Login error:", error)
      
      if (error instanceof Error) {
        setError(`Connection failed: ${error.message}`)
      } else {
        setError("Connection failed. Please check your network and try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }, [username, password, isBlocked, onLoginSuccess, attemptCount])

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev)
  }, [])

  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value)
    if (error) setError("") // Clear error when user starts typing
  }, [error])

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (error) setError("") // Clear error when user starts typing
  }, [error])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse delay-500"></div>
      </div>

      <Card className="w-full max-w-md bg-slate-800/50 backdrop-blur-xl border-slate-700 shadow-2xl relative z-10">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-white">🐘⚡ Gajah Terbang</CardTitle>
            <CardDescription className="text-slate-300">Advanced Anti-Bot Dashboard</CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-200 font-medium">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                placeholder="Enter username"
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-purple-400 focus:ring-purple-400/20"
                disabled={isLoading || isBlocked}
                autoComplete="username"
                required
                aria-describedby={error ? "error-message" : undefined}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-200 font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter password"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 pr-10 focus:border-purple-400 focus:ring-purple-400/20"
                  disabled={isLoading || isBlocked}
                  autoComplete="current-password"
                  required
                  aria-describedby={error ? "error-message" : undefined}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 text-slate-400 hover:text-white hover:bg-transparent"
                  onClick={togglePasswordVisibility}
                  disabled={isLoading || isBlocked}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {error && (
              <Alert className="bg-red-900/50 border-red-700 text-red-200" role="alert">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription id="error-message">{error}</AlertDescription>
              </Alert>
            )}

            {attemptCount > 0 && attemptCount < maxAttempts && (
              <Alert className="bg-yellow-900/50 border-yellow-700 text-yellow-200">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  Warning: {maxAttempts - attemptCount} attempt(s) remaining
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || isBlocked || !username.trim() || !password.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Authenticating...
                </>
              ) : isBlocked ? (
                "Access Blocked"
              ) : (
                "Access Dashboard"
              )}
            </Button>
          </form>

          {/* Only show default credentials in development */}
          {process.env.NODE_ENV === 'development' && CONFIG?.DEFAULT_ADMIN && (
            <div className="text-center space-y-2 p-3 bg-slate-700/30 rounded-lg border border-slate-600/50">
              <p className="text-xs text-slate-400">Development credentials:</p>
              <p className="text-xs text-slate-300 font-mono">
                {CONFIG.DEFAULT_ADMIN.USERNAME} / {CONFIG.DEFAULT_ADMIN.PASSWORD}
              </p>
            </div>
          )}

          <div className="text-center text-xs text-slate-500 space-y-1">
            <p>🛡️ Secured by Advanced Authentication</p>
            <p>Real-Time Monitoring • Intelligence Engine • Campaign Control</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
